/**
 * Popup de productos.
 *
 * Nunca saca al mesero de la pantalla del pedido: se abre encima, cada toque
 * suma una unidad y el pie muestra el total en vivo. Es la interacción que
 * más veces se repite en un turno, así que está optimizada para tocar rápido
 * y cerrar, no para navegar.
 *
 * Los productos agotados se muestran igual, deshabilitados. Ocultarlos haría
 * que el mesero los buscara sin entender por qué no aparecen.
 */

import { useMemo, useState } from 'react';
import { cop, pad2 } from '../lib/formato';
import type { ItemLocal, OrdenLocal } from '../datos/db';
import type { CatalogoVista } from '../datos/vistas';
import type { Producto } from '../tipos/contrato';

interface Props {
  catalogo: CatalogoVista | undefined;
  orden: OrdenLocal;
  items: ItemLocal[];
  subtotal: number;
  iconoDe: (productoId: string) => string;
  onAgregar: (p: Producto) => void;
  onCerrar: () => void;
}

export function ModalProductos({
  catalogo,
  orden,
  items,
  subtotal,
  iconoDe,
  onAgregar,
  onCerrar,
}: Props) {
  const [categoria, setCategoria] = useState<string>('todos');
  const [busqueda, setBusqueda] = useState('');

  const cantidades = useMemo(() => {
    const m = new Map<string, number>();
    for (const i of items) m.set(i.productoId, (m.get(i.productoId) ?? 0) + i.cantidad);
    return m;
  }, [items]);

  const visibles = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return (catalogo?.productos ?? []).filter(
      (p) =>
        (categoria === 'todos' || p.categoriaId === categoria) &&
        (q === '' || p.nombre.toLowerCase().includes(q)),
    );
  }, [catalogo, categoria, busqueda]);

  return (
    <div className="overlay on" onMouseDown={(e) => e.target === e.currentTarget && onCerrar()}>
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby="tituloProductos">
        <header>
          <h2 id="tituloProductos">Agregar productos</h2>
          <span className="mono" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            Mesa {pad2(orden.mesaNumero ?? '—')} · pedido #{orden.numero}
          </span>
          <button className="close" onClick={onCerrar} aria-label="Cerrar">
            ✕
          </button>
        </header>

        <div className="body">
          <div className="search">
            <input
              autoFocus
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar plato o bebida…"
              autoComplete="off"
            />
          </div>

          <div className="chips">
            <button
              className="chip"
              aria-pressed={categoria === 'todos'}
              onClick={() => setCategoria('todos')}
            >
              Todos
            </button>
            {(catalogo?.categorias ?? []).map((c) => (
              <button
                key={c.id}
                className="chip"
                aria-pressed={categoria === c.id}
                onClick={() => setCategoria(c.id)}
              >
                {c.nombre}
              </button>
            ))}
          </div>

          <div className="pgrid">
            {visibles.map((p) => {
              const enMesa = cantidades.get(p.id) ?? 0;
              return (
                <button
                  key={p.id}
                  className="pcard"
                  onClick={() => onAgregar(p)}
                  disabled={!p.disponible}
                  style={p.disponible ? undefined : { opacity: 0.45, cursor: 'not-allowed' }}
                  title={p.disponible ? p.nombre : `${p.nombre} — agotado`}
                >
                  {enMesa > 0 && <span className="badge mono">{enMesa}</span>}
                  <span className="e">{iconoDe(p.id)}</span>
                  <span className="pn">{p.nombre}</span>
                  <span className="pp mono">{cop(p.precio)}</span>
                </button>
              );
            })}

            {visibles.length === 0 && (
              <p className="none">Ningún producto coincide con la búsqueda.</p>
            )}
          </div>
        </div>

        <footer>
          <div style={{ flex: 1 }}>
            <div className="eyebrow">En la mesa</div>
            <div className="mono" style={{ fontSize: 17, fontWeight: 700 }}>
              {cop(subtotal)}
            </div>
          </div>
          <button className="btn btn-primary" onClick={onCerrar}>
            Listo
          </button>
        </footer>
      </div>
    </div>
  );
}
